/**
 * Shared cell creation utilities for layout strategies.
 *
 * These functions handle the common logic of creating GridCell objects
 * from run data, avoiding duplication across different layout strategies.
 */

import {
  isMediaCardType,
  type MaskOptions,
  type MediaCardMetadata,
  type MediaCardType,
} from '@wandb/weave/common/types/media';

import {type RunSignature} from '../../../../types/run';
import {
  getMediaMetadata,
  getRawCaptionsFromMetadata,
} from '../../../../util/media';
import {createTileMediaSpec} from '../../../MediaCard';
import {type MediaBrowserPanelConfig} from '../../common';
import {type StepMetaRun} from '../../types';
import {defaultLayout} from '../../utils/maskControlUtils';
import {getStepToShow} from '../../utils/stepUtils';
import {type GridCell, type TransformInput} from '../types';

/**
 * Creates cells for all media keys at a given run/step/index combination.
 *
 * @param run - The run to create cells for
 * @param stepContext - Complete step information (step number, slider key, slider value)
 * @param mediaIndex - The media index
 * @param mediaKeys - Array of media keys to create cells for
 * @param config - Panel configuration
 * @param allMasks - Mask configuration
 * @param expectedMediaType - Optional pre-computed media type (optimization to avoid repeated lookups)
 * @returns Array of grid cells (may be empty if no data exists)
 */
export function createCellsForMediaKeys(
  run: TransformInput['runs'][0],
  runStepMeta: StepMetaRun,
  currentStep: number,
  mediaIndex: number,
  mediaKeys: string[],
  config: TransformInput['config'],
  allMasks: TransformInput['allMasks'],
  expectedMediaType: MediaCardType
): GridCell[] {
  const cells: GridCell[] = [];

  for (const mediaKey of mediaKeys) {
    const cell = createCell(
      run,
      runStepMeta,
      currentStep,
      mediaIndex,
      mediaKey,
      config,
      allMasks,
      expectedMediaType
    );
    if (cell) {
      cells.push(cell);
    }
  }

  return cells;
}

/**
 * Creates a single grid cell for a specific media key.
 *
 * @param run - The run to create a cell for
 * @param runStepMeta - The run step meta to create a cell for
 * @param currentStep - Step value for the cell (step number, timestamp, etc.)
 * @param mediaIndex - The media index
 * @param mediaKey - The media key to create a cell for
 * @param config - Panel configuration with media slider key
 * @param allMasks - Mask configuration
 * @param expectedMediaType - Optional pre-computed media type (optimization to avoid repeated lookups)
 * @returns Grid cell or null if no metadata exists for this combination
 */
export function createCell(
  run: TransformInput['runs'][0],
  runStepMeta: StepMetaRun,
  currentStep: number,
  mediaIndex: number,
  mediaKey: string,
  config: TransformInput['config'],
  allMasks: TransformInput['allMasks'],
  expectedMediaType: MediaCardType
): GridCell | null {
  const sliderKey = config.mediaSliderKey ?? '_step';

  const runSignature: RunSignature = {
    entityName: run.entityName,
    projectName: run.projectName,
    runName: run.name,
  };

  const maskOptions = getMaskOptionsForCell(mediaKey, config, allMasks);

  const {step: stepToShow, sliderStepValue} = getStepToShow(
    runStepMeta,
    sliderKey,
    currentStep,
    config.snapToExistingStep
  );

  // In compare mode with fan-out on step, if the run didn't log at the exact
  // requested step and snap-to-step is enabled, mark the cell as not found
  // rather than showing media from a previous step.
  const isFanOutStepNotFound =
    config.mode !== 'compare'
      ? undefined
      : config.compareSettings?.fanOutVariable === 'step' &&
        config.snapToExistingStep &&
        sliderStepValue !== currentStep;

  // Check if this cell has captions (computed once during cell creation)
  // Use stepToShow to find the actual history row with media
  const historyRow = run.history?.find(
    hr => hr[mediaKey] && hr._step === stepToShow
  );
  const cellMetadata = historyRow?.[mediaKey];

  // Without this check, the fetch layer falls back to a previous step
  // that had more items logged.
  if (config.compareSettings?.fanOutVariable === 'index' && cellMetadata) {
    const mediaCount = (cellMetadata.count ?? 1) / (cellMetadata.grouping || 1);
    if (mediaIndex >= mediaCount) {
      return createEmptyCell(mediaKey);
    }
  }

  const hasCaption =
    !isFanOutStepNotFound && cellMetadata
      ? getRawCaptionsFromMetadata(cellMetadata, mediaIndex)
          .map(caption => String(caption).trim())
          .filter(Boolean).length > 0
      : false;

  return {
    run,
    step: currentStep,
    mediaIndex,
    mediaKey,
    tileSpec: createTileMediaSpec({
      mediaType: expectedMediaType,
      run,
      mediaStep: stepToShow, // The _step number (possibly after snapping) for backend lookups
      mediaIndex,
      mediaKey,
      mediaSliderKey: sliderKey,
      mediaSliderKeyValue: sliderStepValue,
      runSignature,
    }),
    labels: {
      run: run.displayName || run.name,
      step: currentStep, // Display the _step number
      index: mediaIndex,
    },
    maskOptions,
    hasCaption,
    hasMetadata: !!cellMetadata,
    isFanOutStepNotFound: isFanOutStepNotFound || undefined,
  };
}

/**
 * Creates an empty cell placeholder for filling column count.
 * Used in grid layout when we need to pad rows to a specific column count.
 *
 * @param mediaKey - The media key for this empty cell
 * @returns Empty grid cell
 */
export function createEmptyCell(mediaKey: string): GridCell {
  return {
    isEmpty: true,
    mediaKey,
  };
}

/**
 * Resolves media type for a media key from run history.
 * This is a performance optimization - call once per media key and pass result to createCell.
 *
 * @param run - The run to search for metadata
 * @param mediaKey - The media key to look up
 * @returns Media type or null if not found
 */
export function resolveMediaTypeForKey(
  run: TransformInput['runs'][0],
  mediaKey: string
): MediaCardType | null {
  // Try to infer type from any row with the media key
  const metadata = run.history?.find(hr => mediaKey in hr)?.[mediaKey];
  return metadata ? getMediaTypeFromMetadata(metadata) : null;
}

/**
 * Resolves media metadata and type for a cell.
 *
 * Strategy:
 * 1. Use getMediaMetadata to find metadata (exact match or fallback)
 * 2. If found, check if it was an exact match
 *    - Exact match: return {metadata, mediaType} (resolvable)
 *    - Fallback: return {metadata: null, mediaType} (not resolvable, but we know the type)
 * 3. If not found: try to infer type from any row with both keys
 * 4. Return {metadata: null, mediaType: inferred} or null (cannot infer)
 *
 * Note: getMediaMetadata may return metadata from a row without the stepKey,
 * so we verify exact match to determine if data is resolvable.
 *
 * @param run - The run to search for metadata
 * @param mediaKey - The media key to look up
 * @param step - The raw step value (e.g., timestamp, wall_time, or _step value) for lookup and display
 * @param sliderStepValue - Unused (kept for backwards compatibility during refactor)
 * @param sliderKeyToMatch - The resolved slider key to match against
 * @returns Object with metadata and mediaType, or null if not found
 */
export function resolveMediaTypeAndMetadata(
  run: TransformInput['runs'][0],
  mediaKey: string,
  sliderStepValue: number,
  sliderKeyToMatch: string
): {metadata: MediaCardMetadata | null; mediaType: MediaCardType} | null {
  // Use getMediaMetadata which tries exact match, then falls back to first row with media key
  // Always use the raw step value for lookup
  const metadata = getMediaMetadata(
    run.history ?? [],
    mediaKey,
    sliderStepValue,
    sliderKeyToMatch
  );

  if (metadata) {
    return {
      metadata,
      mediaType: getMediaTypeFromMetadata(metadata),
    };
  }

  // No metadata found - try to infer type from any row with the media key
  // We don't require the slider key to be present for inference
  const inferenceMetadata = run.history?.find(hr => mediaKey in hr)?.[mediaKey];

  return inferenceMetadata
    ? {metadata: null, mediaType: getMediaTypeFromMetadata(inferenceMetadata)}
    : null;
}

/**
 * Determines the media type from metadata object.
 *
 * @param metadata - Media metadata from run history
 * @returns Media card type
 */
function getMediaTypeFromMetadata(metadata: any): MediaCardType {
  const type = metadata._type;

  // Most metadata._type values already match MediaCardType
  // Use centralized type guard from common/types/media
  if (isMediaCardType(type)) {
    return type;
  }

  // Fallback to 'image' for unknown/legacy types (e.g., 'images-file')
  return 'image';
}

/**
 * Gets mask options for a cell from configuration.
 *
 * @param mediaKey - The media key
 * @param config - Panel configuration
 * @param allMasks - All available masks (used to generate default layout when not configured)
 * @returns Mask options or undefined if not configured
 */
function getMaskOptionsForCell(
  mediaKey: string,
  config: MediaBrowserPanelConfig,
  allMasks: any
): MaskOptions | undefined {
  // Extract mask keys from allMasks for this media key
  const maskKeys = Object.keys(allMasks[mediaKey] ?? {});

  // Get tileLayout configuration, falling back to defaultLayout if not configured
  const tileLayout = config.tileLayout;
  const maskConfig = tileLayout?.[mediaKey] ?? defaultLayout(maskKeys);

  if (!maskConfig.maskOptions || maskConfig.maskOptions.length === 0) {
    return undefined;
  }

  return maskConfig.maskOptions[0];
}
